import { useMemo } from 'react';
import startCase from 'lodash/startCase';

import {
  ContentStaticTeachingLearningObjectType,
  LEARNING_SEQUENCE_CATEGORY_INTENTION,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES,
  LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB,
  LEARNING_SEQUENCE_ITEM_TYPE_INTRODUCTION,
} from 'global/constants';
import { TeachingLearningSequenceRichtextTiptap } from './teaching-learning-sequence-richtext-tiptap';
import { Heading18 } from 'approot/shared/styles/typography.style';
import { TeachingAndLearningSequence } from './teaching-learning-sequence.constants';
import {
  getNotes,
  getRichtextContent,
  hasSequenceContent,
  isStaticContent,
} from './teaching-learning-sequence.utils';
import * as Styled from './teaching-learning-sequence.style';
import TeachingLearningSequenceInlineSvg from 'approot/shared/inline-svg/teaching-learning-sequence/teaching-learning-sequence-inline-svg';
import { TeachingLearningSequenceRichtextView } from './teaching-learning-sequence-richtext';
import TeachingLearningSequenceNotesView from './teaching-learning-sequence-notes-view';

type Props = {
  contentType: ContentStaticTeachingLearningObjectType;
  sequences: TeachingAndLearningSequence[] | null;
  isSmall?: boolean;
};

const TeachingLearningSequenceIntroductionView = ({
  sequences,
  isSmall,
  contentType,
}: Props) => {
  const { notes, learningIntention } = useMemo(() => {
    const allNotes = getNotes({
      itemType: LEARNING_SEQUENCE_ITEM_TYPE_INTRODUCTION,
      sequences,
    });

    const learningIntention = allNotes?.find(
      note =>
        note.category === LEARNING_SEQUENCE_CATEGORY_INTENTION &&
        note.sort === null
    );

    return {
      notes: allNotes?.filter(
        note =>
          note.id !== learningIntention?.id &&
          // hide Lesson Materials in TLS Introduction Section
          note.category !== LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED
      ),
      learningIntention,
    };
  }, [sequences]);

  if (
    !hasSequenceContent([
      ...(notes || []),
      ...(learningIntention ? [learningIntention] : []),
    ] as TeachingAndLearningSequence[])
  )
    return null;

  return (
    <Styled.TeachingLearningSequenceViewContainer>
      <Styled.TeachingLearningSequenceViewHeader>
        <Styled.TeachingLearningSequenceIcon>
          <TeachingLearningSequenceInlineSvg
            id="icon_lightning"
            width={16}
            height={16}
          />
        </Styled.TeachingLearningSequenceIcon>

        <Heading18 as="h3" marginBottom={0}>
          {startCase(contentType)} Introduction
        </Heading18>
      </Styled.TeachingLearningSequenceViewHeader>
      <Styled.TeachingLearningSequenceViewContent>
        {isStaticContent(learningIntention?.data) ? (
          <TeachingLearningSequenceRichtextView
            title="Learning Intention"
            content={getRichtextContent(learningIntention?.data)}
            isSmall={isSmall}
          />
        ) : (
          <TeachingLearningSequenceRichtextTiptap
            title="Learning Intention"
            content={getRichtextContent(learningIntention?.data)}
            isSmall={isSmall}
          />
        )}

        <TeachingLearningSequenceNotesView
          notes={notes}
          categories={
            isStaticContent(notes?.[0]?.data)
              ? LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES
              : LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB
          }
          isSmall={isSmall}
        />
      </Styled.TeachingLearningSequenceViewContent>
    </Styled.TeachingLearningSequenceViewContainer>
  );
};

export default TeachingLearningSequenceIntroductionView;
